import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, 
  FileSpreadsheet, 
  PlusCircle, 
  TrendingUp,
  TrendingDown,
  Clock,
  AlertCircle,
  Star,
  XCircle,
  ArrowRight,
  Loader2,
  Activity,
  ScanEye,
  HelpCircle,
  Ruler,
  CircleDollarSign,
  Rocket,
  Info,
  Tag,
  Flag,
  Sparkles,
  X,
  ClipboardList,
  ArrowLeftRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  RoiValue,
  hasRealRoiVenta,
  getRoiVentaValue,
} from '../components/RoiDisplay';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [defaultFilter, setDefaultFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missingModal, setMissingModal] = useState(null); // { bucket, title }
  const [missingData, setMissingData] = useState(null);
  const [missingLoading, setMissingLoading] = useState(false);
  const [pendientesCount, setPendientesCount] = useState(0);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    const refresh = () => axios.get(`${API}/assets/stats`, { withCredentials: true }).then(r => setStats(r.data)).catch(() => {});
    window.addEventListener('xenicos:refresh-cartera', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('xenicos:refresh-cartera', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  // Fetch the "Pendientes de revisión" counter for the widget.
  // Hidden for inversor — also no need to fetch.
  useEffect(() => {
    if (!user || user?.role === 'inversor') return;
    let active = true;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API}/me/pendientes-revision/count`, { withCredentials: true });
        if (active) setPendientesCount(r.data?.count || 0);
      } catch { /* noop */ }
    };
    fetchCount();
    const onRefresh = () => fetchCount();
    window.addEventListener('xenicos:refresh-pendientes', onRefresh);
    window.addEventListener('xenicos:refresh-bell', onRefresh);
    return () => {
      active = false;
      window.removeEventListener('xenicos:refresh-pendientes', onRefresh);
      window.removeEventListener('xenicos:refresh-bell', onRefresh);
    };
  }, [user]);

  // Convert a saved-filter snapshot into URLSearchParams for the health endpoint
  const filterToSearchParams = (snap) => {
    const p = new URLSearchParams();
    if (!snap) return p;
    const multi = ['provincia', 'municipio', 'proveedor', 'estado', 'motivoDescarte', 'fase', 'prioridad', 'semaforo', 'accion', 'tipoOperativo', 'tipoInmueble', 'posesion'];
    const map = {
      accion: 'accion_recomendada',
      tipoOperativo: 'tipo_activo_operativo',
      tipoInmueble: 'tipo_activo',
      motivoDescarte: 'motivo_descarte',
    };
    multi.forEach(k => {
      const v = snap[k];
      if (Array.isArray(v) && v.length) p.append(map[k] || k, v.join(','));
    });
    if (snap.search) p.append('search', snap.search);
    if (snap.sinM2) p.append('sin_metros', 'true');
    if (snap.esTop) p.append('es_top', 'true');
    return p;
  };

  // Build a /activos link that merges the user's default filter (if any)
  // with the extra param for the clicked bucket. The bucket param OVERRIDES
  // any same-named param coming from the default filter so that e.g. clicking
  // "Revisar" on a default filter of `estado=activo` still navigates to
  // `estado=revisar`.
  const buildActivosLink = (extra = {}) => {
    const p = filterToSearchParams(defaultFilter?.filters || {});
    Object.entries(extra).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') return;
      p.set(k, String(v));
    });
    const qs = p.toString();
    return qs ? `/activos?${qs}` : '/activos';
  };

  const fetchAll = async () => {
    try {
      // 1) Load saved filters first so we know whether to scope the health panel
      let defFilter = null;
      try {
        const fr = await axios.get(`${API}/saved-filters`, { withCredentials: true });
        defFilter = ((fr.data.items ?? fr.data) || []).find(f => f.is_default) || null;
      } catch { /* no filters → fall back to global health */ }
      setDefaultFilter(defFilter);

      // 2) Load stats + health in parallel. Health uses the filter params if present.
      const healthUrl = defFilter
        ? `${API}/dashboard/scoring-health?${filterToSearchParams(defFilter.filters).toString()}`
        : `${API}/dashboard/scoring-health`;
      const [s, h] = await Promise.all([
        axios.get(`${API}/assets/stats`, { withCredentials: true }),
        axios.get(healthUrl, { withCredentials: true }),
      ]);
      setStats(s.data);
      setHealth(h.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  // Open the "missing data" modal for a given data-quality bucket and fetch
  // the per-asset breakdown so the user sees exactly what's missing.
  const openMissingModal = async (bucket, title) => {
    setMissingModal({ bucket, title });
    setMissingLoading(true);
    setMissingData(null);
    try {
      const base = defaultFilter
        ? `${filterToSearchParams(defaultFilter.filters).toString()}&`
        : '';
      const url = `${API}/dashboard/missing-scoring-details?${base}bucket=${bucket}&limit=50`;
      const r = await axios.get(url, { withCredentials: true });
      setMissingData(r.data);
    } catch (e) {
      toast.error('No se pudieron cargar los detalles');
      setMissingModal(null);
    } finally {
      setMissingLoading(false);
    }
  };

  // Tarjetas SECUNDARIAS — datos de consulta (izquierda, tamaño reducido).
  // (Feb 2026) Se eliminan las cards duplicadas por el Flujo de Adquisición
  // ("Nuevos" con estado=nuevo — no existe — y "En Fase Revisión" — campo fase
  // retirado). Papelera se conserva porque es un enlace de utilidad al trash.
  const secondaryStatCards = stats ? [
    { label: 'Total Activos', value: stats.total, icon: Building2, color: 'blue', link: '/activos' },
    { label: 'Papelera', value: stats.descartados ?? 0, icon: XCircle, color: 'slate', link: '/activos?estado=papelera' },
  ] : [];

  // Tarjetas PRIMARIAS — datos de acción (derecha, destacadas).
  // (Feb 2026) "En Mediación" y "Nuevos (import)" retiradas: la primera vive
  // en el Flujo de Adquisición, la segunda queda representada por la fase
  // NUEVO. El resto son indicadores de acción cross-fase.
  const primaryStatCards = stats ? [
    { label: 'Prioritarios', value: stats.prioritarios, icon: Star, color: 'orange', link: '/activos?prioridad=alta' },
    { label: 'Solicitada Revisión', value: stats.pendientes_revision ?? 0, icon: Flag, color: 'amber', link: '/activos?pendiente_revision=true', testid: 'stat-solicitada-revision' },
    { label: 'En Descuento', value: stats.en_descuento ?? 0, icon: TrendingDown, color: 'red', link: '/activos?en_descuento=true', testid: 'stat-en-descuento' },
    { label: 'Cambios de cartera', value: stats.cambios_cartera ?? 0, icon: ArrowLeftRight, color: 'cyan', link: '/cambios-cartera', testid: 'stat-cambios-cartera' },
  ] : [];

  // FASES del proceso de adquisición (spec Feb 2026). Orden exacto.
  // Estética uniforme: sin colores propios por fase. El diseño se apoya en
  // el acento cian corporativo y el fondo elevado del dark mode.
  const FASE_FLUJO = [
    { key: 'nuevo',          label: 'NUEVO' },
    { key: 'estudio',        label: 'ESTUDIO' },
    { key: 'mediacion',      label: 'MEDIACIÓN' },
    { key: 'en_gestion',     label: 'EN GESTIÓN' },
    { key: 'oferta',         label: 'OFERTA' },
    { key: 'proceso_compra', label: 'PROCESO COMPRA' },
    { key: 'adquirido',      label: 'ADQUIRIDO' },
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 text-blue-600 border-blue-100',
      emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
      amber: 'bg-amber-50 text-amber-600 border-amber-100',
      violet: 'bg-violet-50 text-violet-600 border-violet-100',
      orange: 'bg-orange-50 text-orange-600 border-orange-100',
      slate: 'bg-slate-50 text-slate-600 border-slate-100',
    };
    return colors[color] || colors.blue;
  };

  // Clases de color destacadas para tarjetas primarias (fondo intenso).
  const getPrimaryColorClasses = (color) => {
    const colors = {
      violet:  { icon: 'bg-violet-500 text-white',  ring: 'ring-violet-200/70',  border: 'border-violet-300',  gradient: 'from-violet-50 to-white' },
      orange:  { icon: 'bg-orange-500 text-white',  ring: 'ring-orange-200/70',  border: 'border-orange-300',  gradient: 'from-orange-50 to-white' },
      amber:   { icon: 'bg-amber-500 text-white',   ring: 'ring-amber-200/70',   border: 'border-amber-300',   gradient: 'from-amber-50 to-white' },
      emerald: { icon: 'bg-emerald-500 text-white', ring: 'ring-emerald-200/70', border: 'border-emerald-300', gradient: 'from-emerald-50 to-white' },
      red:     { icon: 'bg-red-500 text-white',     ring: 'ring-red-200/70',     border: 'border-red-300',     gradient: 'from-red-50 to-white' },
      cyan:    { icon: 'bg-cyan-600 text-white', ring: 'ring-cyan-200/70', border: 'border-cyan-300', gradient: 'from-cyan-50 to-white' },
    };
    return colors[color] || colors.violet;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-1">Dashboard</h1>
          <p className="body-text mt-1">Visión general de tu cartera de activos</p>
        </div>
        <div className="flex gap-3">
          <Link to="/importar">
            <button className="btn-primary flex items-center gap-2" data-testid="import-btn">
              <FileSpreadsheet className="w-4 h-4" />
              Importar Excel
            </button>
          </Link>
          <Link to="/crear-activo">
            <button className="btn-secondary flex items-center gap-2" data-testid="create-asset-btn">
              <PlusCircle className="w-4 h-4" />
              Crear Activo
            </button>
          </Link>
        </div>
      </div>

      {/* Pendientes de revisión — widget rápido (oculto para inversor) */}
      {user?.role !== 'inversor' && (
        <Link
          to="/pendientes-revision"
          className="block rounded-xl p-4 transition-all hover:scale-[1.005]"
          style={{
            background: pendientesCount > 0
              ? 'linear-gradient(90deg, rgba(251, 191, 36, 0.10) 0%, rgba(239, 68, 68, 0.08) 100%)'
              : 'linear-gradient(90deg, rgba(16, 185, 129, 0.08) 0%, rgba(34, 211, 238, 0.06) 100%)',
            border: `1px solid ${pendientesCount > 0 ? 'rgba(251, 191, 36, 0.35)' : 'rgba(16, 185, 129, 0.30)'}`,
          }}
          data-testid="dashboard-pendientes-widget"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  background: pendientesCount > 0 ? 'rgba(251, 191, 36, 0.18)' : 'rgba(16, 185, 129, 0.18)',
                  color: pendientesCount > 0 ? '#fcd34d' : '#6ee7b7',
                }}
              >
                <ClipboardList className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  {pendientesCount > 0
                    ? `Tienes ${pendientesCount} pendiente${pendientesCount === 1 ? '' : 's'} de revisión`
                    : 'No tienes pendientes de revisión'}
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                  {pendientesCount > 0
                    ? 'Activos con cambios marcados para que les eches un ojo.'
                    : 'Todo al día. Si surgen novedades aparecerán aquí.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--color-blue-accent)' }}>
              <span className="hidden sm:inline">Ir a Pendientes</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </Link>
      )}

      {/* Stats Grid — DOS COLUMNAS.
          IZQUIERDA (secundarias, 2x2 pequeñas): datos de consulta.
          DERECHA (primarias, 3 grandes): datos de ACCIÓN destacados. */}
      <div className="grid grid-cols-1 xl:grid-cols-6 gap-4">
        {/* Secundarias — 4 tarjetas compactas en 2×2 */}
        <div className="xl:col-span-2 grid grid-cols-2 gap-3" data-testid="stats-secondary">
          {secondaryStatCards.map((stat) => (
            <Link
              key={stat.label}
              to={stat.link}
              className="card-base p-3 cursor-pointer hover:shadow-sm transition-shadow flex flex-col items-center text-center"
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className={`w-8 h-8 rounded-md flex items-center justify-center mb-1.5 ${getColorClasses(stat.color)}`}>
                <stat.icon className="w-4 h-4" />
              </div>
              <p className="text-xl font-bold leading-none" style={{ color: 'var(--color-text-primary)' }}>{(stat.value ?? 0).toLocaleString('es-ES')}</p>
              <p className="text-[10px] mt-1 font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>{stat.label}</p>
            </Link>
          ))}
        </div>
        {/* Primarias — 3 tarjetas grandes destacadas (cross-fase) */}
        <div className="xl:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stats-primary">
          {primaryStatCards.map((stat) => {
            const c = getPrimaryColorClasses(stat.color);
            return (
              <Link
                key={stat.label}
                to={stat.link}
                className={`relative rounded-xl p-3 lg:p-4 cursor-pointer transition-all border-2 ${c.border} ring-4 ${c.ring} hover:ring-8 hover:shadow-lg flex flex-col items-center text-center`}
                style={{ background: 'var(--color-bg-elevated)' }}
                data-testid={stat.testid || `stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 shadow ${c.icon}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <p data-testid={`${stat.testid || `stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}-count`} className="text-3xl font-extrabold leading-none" style={{ color: 'var(--color-text-primary)' }}>
                  {(stat.value ?? 0).toLocaleString('es-ES')}
                </p>
                <p className="text-[11px] mt-2 font-semibold uppercase tracking-wide leading-tight" style={{ color: 'var(--color-text-primary)' }}>
                  {stat.label}
                </p>
              </Link>
            );
          })}
        </div>
      </div>

      {/* FLUJO DE ADQUISICIÓN — pipeline profesional unificado.
          Estética común (sin colores por fase) — sólo el acento cian
          corporativo y el fondo elevado del dark mode. Los contadores siguen
          alimentándose de `stats.flujo_adquisicion` (estado ∈ {activo, standby}
          por spec Feb 2026 §3). */}
      <div className="card-base p-4 lg:p-5" data-testid="flujo-adquisicion">
        <h3 className="heading-3 mb-4" title="Punto del proceso de adquisición en el que se encuentra">
          Flujo de Adquisición
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex items-stretch gap-1.5">
          {FASE_FLUJO.map((fase, idx) => {
            const count = (stats?.flujo_adquisicion || {})[fase.key] ?? 0;
            return (
              <React.Fragment key={fase.key}>
                <Link
                  to={`/activos?etapa_visual=${fase.key}&estado=activo,standby`}
                  className="flex-1 min-w-0 rounded-lg border p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-shadow hover:border-cyan-500/60 hover:shadow-md"
                  style={{
                    background: 'var(--color-bg-elevated)',
                    borderColor: 'var(--color-border)',
                    minHeight: '96px',
                  }}
                  data-testid={`flujo-card-${fase.key}`}
                  title={`${fase.label}: ${count} activo${count === 1 ? '' : 's'} · Clic para filtrar`}
                >
                  <p
                    className="text-[10px] font-bold uppercase tracking-wide leading-none mb-2 text-center"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {fase.label}
                  </p>
                  <p
                    className="text-3xl font-extrabold leading-none"
                    style={{ color: 'var(--color-blue-accent)' }}
                    data-testid={`flujo-count-${fase.key}`}
                  >
                    {count.toLocaleString('es-ES')}
                  </p>
                </Link>
                {idx < FASE_FLUJO.length - 1 && (
                  <div
                    className="hidden lg:flex items-center justify-center px-0.5 select-none"
                    style={{ color: 'var(--color-text-muted)' }}
                    aria-hidden
                  >
                    <ArrowRight className="w-4 h-4" />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Scoring Health panel */}
      {health && (
        <div className="card-base p-6" data-testid="scoring-health-panel">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="heading-3 flex items-center gap-2">
                <Activity className="w-5 h-5" style={{ color: 'var(--color-success)' }} />
                Salud del Scoring Económico
              </h2>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
                {health.with_score.toLocaleString('es-ES')} de {health.total_visible.toLocaleString('es-ES')} activos con score calculado
                {health.total_visible > 0 && (
                  <span className="ml-1 opacity-80">
                    ({Math.round((health.with_score / health.total_visible) * 100)}%)
                  </span>
                )}
              </p>
              {defaultFilter && (
                <p className="text-xs mt-1 flex items-center gap-1" style={{ color: 'var(--color-blue-accent)' }} data-testid="default-filter-badge">
                  <Star className="w-3 h-3" style={{ fill: 'var(--color-warning)', color: 'var(--color-warning)' }} />
                  Filtro aplicado: <span className="font-semibold">{defaultFilter.name}</span>
                </p>
              )}
            </div>
            <Link to="/activos" className="text-sm flex items-center gap-1 hover:underline" style={{ color: 'var(--color-blue-accent)' }}>
              Ir a Base de Activos <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {/* Data-quality metrics: tarjetas neutras, SOLO icono con color */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            {[
              { to: { pendiente_revision: 'true' }, bucket: null,   icon: Flag,             iconColor: 'var(--color-warning)',    label: 'Solicitada Revisión', value: health.solicitada_revision || 0,   sub: 'activos con revisión solicitada',    testid: 'health-solicitada-revision' },
              { to: { tipo_activo_operativo: 'sindatos' }, bucket: 'sin_tipo_op', icon: Tag,         iconColor: 'var(--color-error)',      label: 'Sin tipo op.',       value: health.sin_tipo_op || 0,           sub: 'sin Crédito/Subasta/Remate…',        testid: 'health-sin-tipo-op' },
              { to: { sin_metros: 'true' }, bucket: 'sin_m2',      icon: Ruler,            iconColor: 'var(--color-text-muted)', label: 'Sin m²',             value: health.sin_m2,                     sub: 'pendientes de consulta al Catastro', testid: 'health-sin-m2' },
              { to: {}, bucket: null,                               icon: CircleDollarSign, iconColor: 'var(--color-blue-accent)', label: 'Precio estimado *',  value: health.precio_estimado_pendiente,  sub: 'con precio calculado por % descuento', testid: 'health-estimado' },
              { to: {}, bucket: 'sin_precio',                       icon: ScanEye,          iconColor: 'var(--color-error)',      label: 'Sin precio',         value: health.sin_precio_compra,          sub: 'sin precio de compra informado',     testid: 'health-sin-precio' },
            ].map((m, i) => (
              <div
                key={i}
                className="relative p-4 rounded-[10px] transition-colors"
                style={{
                  background: 'var(--color-bg-elevated)',
                  border: '1px solid var(--color-border)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; }}
                data-testid={m.testid}
              >
                {m.bucket && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openMissingModal(m.bucket, m.label); }}
                    className="absolute top-2 right-2 p-1 rounded hover:bg-slate-200/60 transition-colors"
                    style={{ color: 'var(--color-text-muted)' }}
                    title="Ver qué datos faltan"
                    data-testid={`${m.testid}-info-btn`}
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                )}
                <Link to={buildActivosLink(m.to)} className="block">
                  <div className="flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
                    <m.icon className="w-4 h-4" style={{ color: m.iconColor }} />
                    <span className="text-xs font-semibold uppercase tracking-wide">{m.label}</span>
                  </div>
                  <p className="text-2xl font-bold mt-2" style={{ color: 'var(--color-text-primary)' }}>
                    {(m.value || 0).toLocaleString('es-ES')}
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{m.sub}</p>
                </Link>
              </div>
            ))}
          </div>

          {/* Action buckets — distribución de acción recomendada.
              Tarjetas neutras; solo el punto-indicador (icono) lleva color. */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-muted)' }}>
              Recomendación de acción
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { key: 'TOP',           label: 'Top oportunidad', iconColor: 'var(--color-success)', value: health.top_oportunidades || 0, sub: 'ROI ≥ 60%', linkQs: { es_top: 'true' },            testid: 'health-bucket-top' },
                { key: 'PRIORIDAD_ALTA', label: 'Prioridad alta',  iconColor: 'var(--color-success)', value: health.buckets?.PRIORIDAD_ALTA || 0, sub: 'ROI 40–60%', linkQs: { accion_recomendada: 'PRIORIDAD_ALTA' }, testid: 'health-bucket-PRIORIDAD_ALTA' },
                { key: 'REVISAR',        label: 'Revisar',         iconColor: 'var(--color-warning)', value: health.buckets?.REVISAR || 0,        sub: 'ROI 20–40%', linkQs: { accion_recomendada: 'REVISAR' },        testid: 'health-bucket-REVISAR' },
                { key: 'STANDBY',        label: 'Standby',         iconColor: '#F97316',             value: health.buckets?.STANDBY || 0,        sub: 'ROI 5–20%',  linkQs: { accion_recomendada: 'STANDBY' },        testid: 'health-bucket-STANDBY' },
                { key: 'DESCARTAR',      label: 'Descartar',       iconColor: 'var(--color-error)',   value: health.buckets?.DESCARTAR || 0,      sub: 'ROI < 5%',   linkQs: { accion_recomendada: 'DESCARTAR' },      testid: 'health-bucket-DESCARTAR' },
              ].map((b) => (
                <Link
                  key={b.key}
                  to={buildActivosLink(b.linkQs)}
                  className="p-3 rounded-[10px] transition-colors"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; }}
                  data-testid={b.testid}
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: b.iconColor }} />
                    <p className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>{b.label}</p>
                  </div>
                  <p className="text-xl font-bold mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {b.value.toLocaleString('es-ES')}
                  </p>
                  <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{b.sub}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions & Recent Assets */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="card-base p-6">
          <h2 className="heading-3 mb-4">Acciones Rápidas</h2>
          <div className="space-y-3">
            <Link 
              to="/importar"
              className="flex items-center justify-between p-4 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors group"
              data-testid="quick-import"
            >
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">Importar listado Excel</span>
              </div>
              <ArrowRight className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link 
              to="/crear-activo"
              className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group"
              data-testid="quick-create"
            >
              <div className="flex items-center gap-3">
                <PlusCircle className="w-5 h-5 text-slate-600" />
                <span className="font-medium text-slate-700">Crear activo manual</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-600 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link 
              to="/activos"
              className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group"
              data-testid="quick-view-all"
            >
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-slate-600" />
                <span className="font-medium text-slate-700">Ver todos los activos</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-600 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Recent Assets */}
        <div className="lg:col-span-2 card-base p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="heading-3">Últimos Activos Actualizados</h2>
            <Link to="/activos" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Ver todos
            </Link>
          </div>
          
          {stats?.recientes?.length > 0 ? (
            <div className="space-y-3">
              {stats.recientes.map((asset) => (
                <Link
                  key={asset.id}
                  to={`/activos/${asset.id}`}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:border-slate-200 hover:bg-slate-50/50 transition-all group"
                  data-testid={`recent-asset-${asset.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {asset.direccion || asset.referencia_catastral || 'Sin dirección'}
                      </p>
                      <p className="text-sm text-slate-500">
                        {asset.municipio} {asset.provincia && `• ${asset.provincia}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getRoiVentaValue(asset) != null && (
                      <RoiValue
                        value={getRoiVentaValue(asset)}
                        isReal={hasRealRoiVenta(asset)}
                        kind="venta"
                        size="sm"
                        testid={`dash-roi-${asset.id}`}
                      />
                    )}
                    <span className={`badge-${
                      asset.estado === 'nuevo' ? 'blue' : 
                      asset.estado === 'activo' ? 'success' : 
                      asset.estado === 'descartado' ? 'neutral' : 'warning'
                    }`}>
                      {asset.estado || 'nuevo'}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No hay activos todavía</p>
              <Link to="/importar" className="text-blue-600 hover:text-blue-700 text-sm font-medium mt-2 inline-block">
                Importa tu primer listado
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Modal: datos faltantes para scoring */}
      {missingModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setMissingModal(null)}
          data-testid="missing-scoring-modal"
        >
          <div
            className="w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-[12px] flex flex-col"
            style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between p-5" style={{ borderBottom: '1px solid var(--color-border)' }}>
              <div>
                <h3 className="heading-3 flex items-center gap-2">
                  <Info className="w-5 h-5" style={{ color: 'var(--color-blue-accent)' }} />
                  Datos faltantes · {missingModal.title}
                </h3>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  {missingLoading
                    ? 'Cargando…'
                    : `${(missingData?.total || 0).toLocaleString('es-ES')} activos en esta categoría. Mostrando ${missingData?.shown || 0}.`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setMissingModal(null)}
                className="p-2 rounded hover:bg-slate-100 transition-colors"
                data-testid="missing-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 overflow-y-auto">
              {missingLoading && (
                <div className="py-10 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--color-text-muted)' }} />
                </div>
              )}

              {!missingLoading && missingData && (
                <>
                  {/* Frequency summary */}
                  {Object.values(missingData.missing_field_counts || {}).some(v => v > 0) && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-muted)' }}>
                        Campos que más faltan (en la muestra)
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(missingData.missing_field_counts || {})
                          .filter(([, n]) => n > 0)
                          .sort(([, a], [, b]) => b - a)
                          .map(([field, count]) => (
                            <span
                              key={field}
                              className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs"
                              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
                            >
                              <span className="font-semibold" style={{ color: 'var(--color-error)' }}>{count}</span>
                              <span style={{ color: 'var(--color-text-secondary)' }}>
                                {missingData.field_labels?.[field] || field}
                              </span>
                            </span>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Assets list */}
                  <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-muted)' }}>
                    Activos afectados
                  </p>
                  {(missingData.items || []).length === 0 ? (
                    <p className="text-sm py-4" style={{ color: 'var(--color-text-muted)' }}>
                      No hay activos en esta categoría con el filtro actual.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {missingData.items.map((a) => (
                        <Link
                          key={a.id}
                          to={`/activos/${a.id}`}
                          className="block p-3 rounded-lg hover:shadow-sm transition-all"
                          style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
                          data-testid={`missing-asset-${a.codigo_npl}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs font-bold" style={{ color: 'var(--color-blue-accent)' }}>
                                  {a.codigo_npl || '—'}
                                </span>
                                <span className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                                  {a.direccion || 'Sin dirección'}
                                </span>
                              </div>
                              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                                {a.municipio || '—'}
                                {a.proveedor && ` · ${a.proveedor}`}
                                {a.tipo_activo_operativo && ` · ${a.tipo_activo_operativo}`}
                              </p>
                              {(a.missing_fields || []).length > 0 ? (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {a.missing_fields.map((f) => (
                                    <span
                                      key={f}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium"
                                      style={{
                                        background: 'color-mix(in oklab, var(--color-error) 12%, transparent)',
                                        color: 'var(--color-error)',
                                      }}
                                    >
                                      Falta: {missingData.field_labels?.[f] || f}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-[11px] mt-2" style={{ color: 'var(--color-success)' }}>
                                  Datos completos — recalcula el scoring desde Parámetros para actualizar el estado.
                                </p>
                              )}
                            </div>
                            <ArrowRight className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: 'var(--color-text-muted)' }} />
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
